import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  ButtonType,
  Flex,
  HeadlineTitle,
  InputWithLabel,
  Loader,
} from '../../../../ui';
import { useAppDispatch, useAppSelector } from '../../../../hooks/appHooks';
import { LoginSelectors, LoginThunks } from '../../login/redux';
import { FooterButtons } from '../../tfa/components/FooterButtons';
import { UserCredentials } from '../../../../types/UserCredentials';
import { FormButtons, FormContainer, FormPane } from '../../../../ui/form';
import { MaxFieldLength } from '../../../../ui/input/common/MaxFieldLength';
import { NoMarginBodyText, StyledTextLink } from '../../tfa/components/styled';
import { ToastType } from '../../../../ui/toast';
import { useToastContainer } from '../../../../layout/toastContainer';
import { useSidePopout } from '../../../../layout';
import { NotifyActionType } from '../../notifyActions/components/TfaNotifyActionSent';

export const TfaMail: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { addToast } = useToastContainer();
  const { closePopout } = useSidePopout();

  const tfaVerifyLoading = useAppSelector(LoginSelectors.selectUserLoading);
  const error = useAppSelector(LoginSelectors.selectTfaVerifyError);
  const [isButtonForNewCodeDisabled, setIsButtonForNewCodeDisabled] =
    useState(false);
  const [
    isButtonForResetTfaProviderDisabled,
    setIsButtonForResetTfaProviderDisabled,
  ] = useState(false);

  const handleVerification = useCallback(
    (event) => {
      event.preventDefault();
      const formData = new FormData(event.target);
      const inputObject = Object.fromEntries(formData) as UserCredentials; // convert the FormData object to a JSON object
      inputObject?.authCode &&
        dispatch(LoginThunks.tfaVerifyCode(inputObject)).then((res) => {
          res.type === 'auth/tfaVerifyCode/fulfilled' && history.push('/');
        });
    },
    [dispatch, history],
  );

  const handleMailCodeResend = () => {
    dispatch(LoginThunks.mailCodeResend()).then((res) => {
      if (res.type === 'auth/mailCodeResend/fulfilled') {
        addToast({
          title: t('landingPage.tfaChoiceEmail.successWithResendMailCodeTitle'),
          text: '',
          type: ToastType.Info,
        });
      } else {
        addToast({
          title: t('landingPage.tfaChoiceEmail.errorWithResendMailCodeTitle'),
          text: t(
            'landingPage.tfaChoiceEmail.errorWithResendMailCodeDescription',
          ),
          type: ToastType.Urgent,
        });
      }
    });
  };

  const resetTfaProviderDispatch = () => {
    dispatch(LoginThunks.resetTfaProvider()).then((res) => {
      if (res.type === 'auth/resetTfaProvider/fulfilled') {
        redirectToTfaNotifyActionSent();
      } else {
        addToast({
          title: t('landingPage.tfaChoiceEmail.errorWithResetTfaProviderTitle'),
          text: t(
            'landingPage.tfaChoiceEmail.errorWithResetTfaProviderDescription',
          ),
          type: ToastType.Urgent,
        });
      }
    });
  };

  const redirectToTfaNotifyActionSent = () => {
    closePopout();
    history.push({
      pathname: '/tfa-reset-notify-action',
      state: { type: NotifyActionType.TFA_PROVIDER_RESET },
    });
  };

  const handleResendMailCode = () => {
    setIsButtonForNewCodeDisabled(true);
    handleMailCodeResend();
    setTimeout(() => setIsButtonForNewCodeDisabled(false), 30000);
  };

  const handleResetTfaProvider = () => {
    setIsButtonForResetTfaProviderDisabled(true);
    resetTfaProviderDispatch();
    setTimeout(() => setIsButtonForResetTfaProviderDisabled(false), 30000);
  };

  return (
    <Loader active={tfaVerifyLoading}>
      <Flex>
        <HeadlineTitle>{t('landingPage.tfaForm.title')}</HeadlineTitle>
        <FormContainer onSubmit={handleVerification}>
          <FormPane>
            <NoMarginBodyText>
              {t('landingPage.tfaForm.introTextForAuthMailScreen')}
              {t('landingPage.tfaForm.switchTextForAuthMailScreen')}
              <StyledTextLink
                type="button"
                buttonType={ButtonType.Text}
                disabled={isButtonForResetTfaProviderDisabled}
                onClick={() => handleResetTfaProvider()}>
                {t('landingPage.tfaChoiceEmail.resetTfaProviderTextLink')}
              </StyledTextLink>
            </NoMarginBodyText>
            <InputWithLabel
              name="authCode"
              label={t('landingPage.tfaChoiceEmail.descriptionText')}
              placeholderText={t(
                'landingPage.tfaChoiceEmail.verifyCodePlaceholder',
              )}
              maxLength={MaxFieldLength.tfaCode}
              errorText={error && t('landingPage.tfaChoiceEmail.error')}
              autoComplete="off"
              autoFocus
            />
          </FormPane>
          <FormButtons
            text={t('landingPage.tfaChoiceEmail.ctaButton')}
            centered
          />
          <FooterButtons />
        </FormContainer>
      </Flex>
    </Loader>
  );
};
